import React, { useCallback, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Alert,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Card,
  CardBody,
  CardTitle,
  ClipboardCopy,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Icon,
  Label,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Tab,
  TabTitleIcon,
  TabTitleText,
  Tabs,
} from '@patternfly/react-core';
import {
  ExclamationCircleIcon,
  MicrosoftIcon,
  PlusCircleIcon,
  PowerOffIcon,
  RedhatIcon,
  RobotIcon,
} from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import CenteredSpinner from '@app/CenteredSpinner';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAgentFeature } from '../../../hooks/useAgentFeature';
import type { AgentCredentials } from '../../../types/models/Agent';
import type { ApiResponseAgentEnable } from '../../../types/api/ApiResponseAgent';
import { DisableAgentModal } from './DisableAgentModal';
import { DeployInstructionsRhel } from './DeployInstructionsRhel';
import { DeployInstructionsWindows } from './DeployInstructionsWindows';
import { DeployInstructionsDebian } from './DeployInstructionsDebian';
import { DebianIcon } from './DebianIcon';

// The agent authenticates against the same Keycloak the Manager uses. These fallbacks cover the
// already-enabled case, where GET /organization/agent returns no credentials object (the POST
// response's `credentials.keycloak_url` / `credentials.realm` win when present).
const DEFAULT_KEYCLOAK_URL = 'https://auth.ancalagon.be';
const DEFAULT_KEYCLOAK_REALM = 'Manager';

/**
 * Organization > Agent — tenant-facing toggle for the Manager Agent feature
 * (GET/POST/DELETE /organization/agent). Enabling provisions a dedicated Keycloak client for
 * the organization's agents and returns its credentials exactly once; this page holds them in
 * state (never re-fetchable) and weaves them into the deployment instructions. Staff
 * impersonation is transparent: the impersonation header makes the backend toggle the
 * impersonated tenant.
 */
export const Agent: React.FC = () => {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const { agent, loading, error, refetch } = useAgentFeature();

  const [enabling, setEnabling] = useState(false);
  // One-time credentials from a fresh enable. Kept only in memory: a token-renew-driven refetch
  // of the status hook must never clobber them, and nothing re-derives them from the API.
  const [credentials, setCredentials] = useState<AgentCredentials | null>(null);
  const [isDisableOpen, setIsDisableOpen] = useState(false);
  // Which platform's deployment instructions are shown in the "Deploy the agent" card.
  const [activeTabKey, setActiveTabKey] = useState<string | number>('rhel');

  const handleEnable = useCallback(async () => {
    setEnabling(true);
    try {
      const response = await apiFetch('/organization/agent', { method: 'POST', cache: 'no-store' });
      const body = (await response.json().catch(() => null)) as ApiResponseAgentEnable | null;

      if (!response.ok || body?.status !== 'success' || !body.data?.agent) {
        throw new Error(body?.message ?? 'Failed to enable the agent feature');
      }

      if (body.data.agent.credentials) {
        setCredentials(body.data.agent.credentials);
        addNotification({
          title: 'Agent enabled',
          srTitle: 'Success alert',
          description:
            'Dedicated agent credentials were created for your organization. The client secret is shown only once — copy it now.',
          variant: 'success',
        });
      } else {
        addNotification({
          title: 'Agent already enabled',
          srTitle: 'Success alert',
          description: 'The agent feature was already enabled for your organization.',
          variant: 'success',
        });
      }
      setDrawerOpen(true);
      await refetch();
    } catch (err) {
      addNotification({
        title: 'Failed to enable agent',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setEnabling(false);
    }
  }, [apiFetch, addNotification, setDrawerOpen, refetch]);

  const handleDisabled = useCallback(async () => {
    setCredentials(null);
    await refetch();
  }, [refetch]);

  const envFileContent = useMemo(() => {
    const clientId = credentials?.client_id ?? agent?.client_id ?? '';
    return [
      `KEYCLOAK_URL=${credentials?.keycloak_url ?? DEFAULT_KEYCLOAK_URL}`,
      `KEYCLOAK_REALM=${credentials?.realm ?? DEFAULT_KEYCLOAK_REALM}`,
      `KEYCLOAK_CLIENT_ID=${clientId}`,
      `KEYCLOAK_CLIENT_SECRET=${credentials?.client_secret ?? '<your-client-secret>'}`,
      '',
      `ANCALAGON_API_URL=${process.env.REACT_APP_BACKENDAPI_URL ?? ''}`,
      '',
      '# Optional: tells the backend which NetBox object type to look up.',
      '# Valid values: device, virtualmachine',
      '# Leave unset to let the backend auto-detect.',
      '#HOST_TYPE=device',
    ].join('\n');
  }, [credentials, agent]);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const showEnableAction = !loading && !error && agent !== null && !agent.enabled;
  const showDisableAction = !loading && !error && agent !== null && agent.enabled;

  const renderEnabled = () => {
    if (!agent) {
      return null;
    }
    return (
      <>
        {credentials ? (
          <Alert
            variant="warning"
            isInline
            className="pf-v6-u-mb-md"
            title="Copy the client secret now — it is shown only once."
          >
            For security reasons the secret cannot be displayed again. If you lose it, disable and re-enable the agent
            to generate new credentials.
          </Alert>
        ) : (
          <Alert
            variant="info"
            isInline
            className="pf-v6-u-mb-md"
            title="The client secret is only shown when the agent is first enabled."
          >
            If you no longer have it, disable and re-enable the agent to generate new credentials, then update your
            deployed agents.
          </Alert>
        )}

        <Card className="pf-v6-u-mb-md">
          <CardTitle>Connection details</CardTitle>
          <CardBody>
            <DescriptionList isHorizontal>
              <DescriptionListGroup>
                <DescriptionListTerm>Status</DescriptionListTerm>
                <DescriptionListDescription>
                  <Label status="success" isCompact>
                    Enabled
                  </Label>
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Client ID</DescriptionListTerm>
                <DescriptionListDescription>
                  {agent.client_id ? (
                    <ClipboardCopy
                      variant="inline-compact"
                      isCode
                      hoverTip="Copy"
                      clickTip="Copied"
                      copyAriaLabel="Copy client ID"
                    >
                      {agent.client_id}
                    </ClipboardCopy>
                  ) : (
                    '—'
                  )}
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Service account ID</DescriptionListTerm>
                <DescriptionListDescription>
                  {agent.service_account_id ? (
                    <ClipboardCopy
                      variant="inline-compact"
                      isCode
                      hoverTip="Copy"
                      clickTip="Copied"
                      copyAriaLabel="Copy service account ID"
                    >
                      {agent.service_account_id}
                    </ClipboardCopy>
                  ) : (
                    '—'
                  )}
                </DescriptionListDescription>
              </DescriptionListGroup>
              {credentials && (
                <DescriptionListGroup>
                  <DescriptionListTerm>Client secret</DescriptionListTerm>
                  <DescriptionListDescription>
                    <ClipboardCopy isReadOnly isCode hoverTip="Copy" clickTip="Copied" copyAriaLabel="Copy client secret">
                      {credentials.client_secret}
                    </ClipboardCopy>
                  </DescriptionListDescription>
                </DescriptionListGroup>
              )}
            </DescriptionList>
          </CardBody>
        </Card>

        <Card>
          <CardTitle>Deploy the agent</CardTitle>
          <CardBody>
            <Tabs
              activeKey={activeTabKey}
              onSelect={(_event, tabKey) => setActiveTabKey(tabKey)}
              aria-label="Agent platform"
            >
              <Tab
                eventKey="rhel"
                title={
                  <>
                    <TabTitleIcon>
                      <RedhatIcon />
                    </TabTitleIcon>
                    <TabTitleText>RHEL based</TabTitleText>
                  </>
                }
              >
                <div className="pf-v6-u-pt-md">
                  <DeployInstructionsRhel envFileContent={envFileContent} hasCredentials={credentials !== null} />
                </div>
              </Tab>
              <Tab
                eventKey="debian"
                title={
                  <>
                    <TabTitleIcon>
                      <DebianIcon />
                    </TabTitleIcon>
                    <TabTitleText>Debian based</TabTitleText>
                  </>
                }
              >
                <div className="pf-v6-u-pt-md">
                  <DeployInstructionsDebian envFileContent={envFileContent} hasCredentials={credentials !== null} />
                </div>
              </Tab>
              <Tab
                eventKey="windows"
                title={
                  <>
                    <TabTitleIcon>
                      <MicrosoftIcon />
                    </TabTitleIcon>
                    <TabTitleText>Windows</TabTitleText>
                  </>
                }
              >
                <div className="pf-v6-u-pt-md">
                  <DeployInstructionsWindows envFileContent={envFileContent} hasCredentials={credentials !== null} />
                </div>
              </Tab>
            </Tabs>
          </CardBody>
        </Card>
      </>
    );
  };

  const renderContent = () => {
    if (loading) {
      return <CenteredSpinner />;
    }

    if (error) {
      return (
        <EmptyState
          titleText="Unable to load the agent status"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => void refetch()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (!agent || !agent.enabled) {
      return (
        <EmptyState titleText="The agent is not enabled" icon={RobotIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>
            Enable the agent feature to create dedicated credentials for your organization&apos;s agents. Deployed
            agents collect read-only system information from your servers and report it to Ancalagon Manager — they
            never modify your hosts.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button
                variant="primary"
                icon={<PlusCircleIcon />}
                onClick={() => void handleEnable()}
                isLoading={enabling}
                isDisabled={enabling}
              >
                Enable agent
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return renderEnabled();
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem>Organization</BreadcrumbItem>
          <BreadcrumbItem isActive>Agent</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex justifyContent={{ default: 'justifyContentSpaceBetween' }} alignItems={{ default: 'alignItemsFlexStart' }}>
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <RobotIcon />
              </Icon>
              Agent
            </Content>
            <Content component="p">
              Deploy read-only agents on your servers to report system information to Ancalagon Manager.
            </Content>
          </FlexItem>
          {showEnableAction && (
            <FlexItem>
              <Button
                variant="primary"
                icon={<PlusCircleIcon />}
                onClick={() => void handleEnable()}
                isLoading={enabling}
                isDisabled={enabling}
              >
                Enable agent
              </Button>
            </FlexItem>
          )}
          {showDisableAction && (
            <FlexItem>
              <Button variant="danger" icon={<PowerOffIcon />} onClick={() => setIsDisableOpen(true)}>
                Disable agent
              </Button>
            </FlexItem>
          )}
        </Flex>
      </PageSection>
      <PageSection hasBodyWrapper={false}>{renderContent()}</PageSection>
      <DisableAgentModal
        isOpen={isDisableOpen}
        onClose={() => setIsDisableOpen(false)}
        clientId={agent?.client_id ?? null}
        onDisabled={handleDisabled}
      />
    </>
  );
};
